"use client";

import { c as _c } from "react/compiler-runtime";
import InfoIcon from "@/assets/icons/info.svg";
import { Tooltip } from "@/shared/ui";
import { cn } from "@/lib/utils";
import { useStreamStatsProgressQuery } from "@/features/stream-stats/api/stream-stats-queries";
import { Card } from "../card/card";
import { GaugeBar, clampPercent } from "../gauge-bar/gauge-bar";
import { splitAirtimeMinutes } from "../../airtime-format";
import streamStatsStyles from "../stream-stats-content/stream-stats-content.module.scss";
import { EmptyStateBadge, ProgressBar, StatusBadge, TARGET_AIRTIME_COLOR, TARGET_SOV_COLOR, TargetIndicator } from "../target-card-parts/target-card-parts";
import { deriveTargetCardState, StatusType } from "../../target-card-state";
import styles from "./target-cards.module.scss";
import { TARGET_CARD_MESSAGES } from "./messages";
import { useCountUp } from "@/shared/hooks";
type ProgressProps = {
  profileId?: string;
  startDate?: string;
  endDate?: string;
  viewingCurrentMonth?: boolean;
};
function formatAirtimeMinutes(minutes: number): string {
  const {
    h,
    m
  } = splitAirtimeMinutes(minutes);
  return h > 0 ? `${h}h ${String(m).padStart(2, "0")}m` : `${m}m`;
}
function formatTargetHours(minutes: number): string {
  return `${Math.round(minutes / 60)}h`;
}
function formatTargetIndicatorValue(targetMinutes: number, source?: string): string {
  if (source === "DEFAULT" || targetMinutes === 0) return "— —";
  if (source === "PERSONAL_BEST") return formatAirtimeMinutes(targetMinutes);
  return formatTargetHours(targetMinutes);
}

/** Render the status pill or empty-state pill based on derived state. */
function renderStatusNode(isFetchFailed: boolean, isNoData: boolean, statusType: ReturnType<typeof deriveTargetCardState>["statusType"]): React.ReactNode {
  if (isFetchFailed) return <StatusBadge type={StatusType.NoDataAvailable} />;
  if (isNoData) return <EmptyStateBadge />;
  return <StatusBadge type={statusType} />;
}
export function AirtimeTargetsCard(t0) {
  const $ = _c(29);
  const {
    className,
    profileId,
    startDate,
    endDate,
    viewingCurrentMonth
  } = t0;
  let t1;
  if ($[0] !== endDate || $[1] !== profileId || $[2] !== startDate) {
    t1 = {
      profileId,
      startDate,
      endDate
    };
    $[0] = endDate;
    $[1] = profileId;
    $[2] = startDate;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    data,
    isLoading,
    isError
  } = useStreamStatsProgressQuery(t1);
  const isFetchFailed = isError && !isLoading;
  const state = deriveTargetCardState({
    currentValue: data != null ? data.airtimeCurrentMinutes ?? 0 : undefined,
    targetValue: data?.airtimeTargetMinutes,
    isCurrentMonth: viewingCurrentMonth ?? data?.viewingCurrentMonth ?? true,
    targetSource: data?.airtimeTargetSource ?? "DEFAULT"
  });
  const targetMinutes = data?.airtimeTargetMinutes ?? 0;
  const currentMinutes = data?.airtimeCurrentMinutes ?? 0;
  const fillPercent = data?.airtimeProgress ?? 0;
  const animatedMinutes = useCountUp(currentMinutes, 1500, 200, 1);
  const animatedPercent = useCountUp(fillPercent, 1700, 280, 1);
  const displayMinutes = isLoading ? 0 : animatedMinutes;
  const displayPercent = isLoading ? 0 : animatedPercent;
  let currentLabel;
  if (isLoading) {
    currentLabel = null;
  } else {
    if (state.isNoData || isFetchFailed) {
      currentLabel = "\u2014 \u2014";
    } else {
      let t2;
      if ($[4] !== displayMinutes) {
        t2 = formatAirtimeMinutes(displayMinutes);
        $[4] = displayMinutes;
        $[5] = t2;
      } else {
        t2 = $[5];
      }
      currentLabel = t2;
    }
  }
  const t2 = data?.airtimeTargetSource;
  let t3;
  if ($[6] !== t2 || $[7] !== targetMinutes) {
    t3 = formatTargetIndicatorValue(targetMinutes, t2);
    $[6] = t2;
    $[7] = targetMinutes;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const indicatorValue = t3;
  const currentAirtime = !isLoading && !state.isNoData && !isFetchFailed ? formatAirtimeMinutes(displayMinutes) : null;
  let t4;
  if ($[9] !== data?.airtimeTargetSource || $[10] !== targetMinutes) {
    t4 = data?.airtimeTargetSource === "CONTRACT" && targetMinutes !== 0 ? formatTargetHours(targetMinutes) : null;
    $[9] = data?.airtimeTargetSource;
    $[10] = targetMinutes;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const barTargetValue = t4;
  let t5;
  if ($[12] !== className) {
    t5 = cn(styles.cardWrap, className);
    $[12] = className;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Tooltip content={TARGET_CARD_MESSAGES.airtimeTooltip} side="top"><button type="button" className={streamStatsStyles.infoButton} aria-label={TARGET_CARD_MESSAGES.airtimeAriaLabel}><InfoIcon className={streamStatsStyles.infoIcon} aria-hidden={true} /></button></Tooltip>;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const t7 = styles;
  const t8 = styles;
  const t9 = currentLabel === null ? <div className={styles.valueSkeletonWide} /> : <p className={state.isNoData ? styles.valueNoData : styles.value}>{currentLabel}</p>;
  const t10 = renderStatusNode(isFetchFailed, state.isNoData, state.statusType);
  let t11;
  if ($[15] !== t10 || $[16] !== t8.cardBodyRow || $[17] !== t9) {
    t11 = <div className={t8.cardBodyRow}>{t9}{t10}</div>;
    $[15] = t10;
    $[16] = t8.cardBodyRow;
    $[17] = t9;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const t12 = state.isNoData || isFetchFailed;
  let t13;
  if ($[19] !== barTargetValue || $[20] !== currentAirtime || $[21] !== displayPercent || $[22] !== state.isTargetReached || $[23] !== t12) {
    t13 = <ProgressBar percent={displayPercent} isNoData={t12} currentAirtime={currentAirtime} targetValue={barTargetValue} isTargetReached={state.isTargetReached} />;
    $[19] = barTargetValue;
    $[20] = currentAirtime;
    $[21] = displayPercent;
    $[22] = state.isTargetReached;
    $[23] = t12;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t11 || $[26] !== t13 || $[27] !== t7.targetCardBody) {
    t14 = <div className={t7.targetCardBody}>{t11}{t13}</div>;
    $[25] = t11;
    $[26] = t13;
    $[27] = t7.targetCardBody;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  return <div className={t5}><Card title={TARGET_CARD_MESSAGES.airtimeTitle} titleInfo={t6} titleRight={<TargetIndicator mode={state.mode} value={indicatorValue} metricColor={TARGET_AIRTIME_COLOR} />} className={styles.airtimeCard}>{t14}</Card></div>;
}
export function ShareOfAirtimeTargetsCard(t0) {
  const $ = _c(31);
  const {
    className,
    profileId,
    startDate,
    endDate,
    viewingCurrentMonth
  } = t0;
  let t1;
  if ($[0] !== endDate || $[1] !== profileId || $[2] !== startDate) {
    t1 = {
      profileId,
      startDate,
      endDate
    };
    $[0] = endDate;
    $[1] = profileId;
    $[2] = startDate;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    data,
    isLoading,
    isError
  } = useStreamStatsProgressQuery(t1);
  const isFetchFailed = isError && !isLoading;
  const state = deriveTargetCardState({
    currentValue: data != null ? data.sovCurrent ?? 0 : undefined,
    targetValue: data?.sovTarget,
    isCurrentMonth: viewingCurrentMonth ?? data?.viewingCurrentMonth ?? true,
    targetSource: data?.sovTargetSource ?? "DEFAULT"
  });
  let t2;
  if ($[4] !== data?.sovCurrent) {
    t2 = Math.round(clampPercent(data?.sovCurrent ?? 0));
    $[4] = data?.sovCurrent;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const currentSov = t2;
  let t3;
  if ($[6] !== data?.sovTarget) {
    t3 = Math.round(clampPercent(data?.sovTarget ?? 0));
    $[6] = data?.sovTarget;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const comparisonSov = t3;
  let t4;
  if ($[8] !== startDate) {
    t4 = startDate ? new Date(startDate).getTime() : 0;
    $[8] = startDate;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const animationTrigger = t4;
  const animatedSov = useCountUp(currentSov, 2500, 200, animationTrigger);
  let currentLabel;
  if (isLoading) {
    currentLabel = null;
  } else {
    if (state.isNoData || isFetchFailed) {
      currentLabel = "\u2014 \u2014";
    } else {
      let t5;
      if ($[10] !== animatedSov) {
        t5 = Math.round(animatedSov);
        $[10] = animatedSov;
        $[11] = t5;
      } else {
        t5 = $[11];
      }
      currentLabel = `${t5}%`;
    }
  }
  let t5;
  if ($[12] !== data?.sovTarget) {
    t5 = data?.sovTarget !== undefined && data?.sovTarget !== 0 ? `${Math.round(data?.sovTarget ?? 0)}%` : "\u2014 \u2014";
    $[12] = data?.sovTarget;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const indicatorValue = t5;
  const displaySov = isLoading ? 0 : animatedSov;
  let t6;
  if ($[14] !== className) {
    t6 = cn(styles.cardWrap, className);
    $[14] = className;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Tooltip content={TARGET_CARD_MESSAGES.shareOfAirtimeTooltip} side="top"><button type="button" className={streamStatsStyles.infoButton} aria-label={TARGET_CARD_MESSAGES.shareOfAirtimeAriaLabel}><InfoIcon className={streamStatsStyles.infoIcon} aria-hidden={true} /></button></Tooltip>;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const t8 = styles;
  const t9 = styles;
  const t10 = currentLabel === null ? <div className={styles.valueSkeletonNarrow} /> : <p className={state.isNoData ? styles.valueNoData : styles.value}>{currentLabel}</p>;
  const t11 = renderStatusNode(isFetchFailed, state.isNoData, state.statusType);
  let t12;
  if ($[17] !== t10 || $[18] !== t11 || $[19] !== t9.cardBodyRow) {
    t12 = <div className={t9.cardBodyRow}>{t10}{t11}</div>;
    $[17] = t10;
    $[18] = t11;
    $[19] = t9.cardBodyRow;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== comparisonSov || $[22] !== displaySov || $[23] !== state.isNoData || $[24] !== state.isTargetReached || $[25] !== state.mode) {
    t13 = <GaugeBar currentSov={displaySov} targetSov={comparisonSov} isNoData={state.isNoData} isReached={state.isTargetReached} mode={state.mode} />;
    $[21] = comparisonSov;
    $[22] = displaySov;
    $[23] = state.isNoData;
    $[24] = state.isTargetReached;
    $[25] = state.mode;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t12 || $[28] !== t13 || $[29] !== t8.targetCardBody) {
    t14 = <div className={t8.targetCardBody}>{t12}{t13}</div>;
    $[27] = t12;
    $[28] = t13;
    $[29] = t8.targetCardBody;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  return <div className={t6}><Card title={TARGET_CARD_MESSAGES.shareOfAirtimeTitle} titleInfo={t7} titleRight={<TargetIndicator mode={state.mode} value={indicatorValue} metricColor={TARGET_SOV_COLOR} />} className={styles.sovCard}>{t14}</Card></div>;
}
export function TargetCardsRow(t0) {
  const $ = _c(12);
  const {
    className,
    profileId,
    startDate,
    endDate,
    viewingCurrentMonth
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn(styles.row, className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  let t3;
  if ($[2] !== endDate || $[3] !== profileId || $[4] !== startDate || $[5] !== viewingCurrentMonth) {
    t2 = <AirtimeTargetsCard className={styles.rowItem} profileId={profileId} startDate={startDate} endDate={endDate} viewingCurrentMonth={viewingCurrentMonth} />;
    t3 = <ShareOfAirtimeTargetsCard className={styles.rowItem} profileId={profileId} startDate={startDate} endDate={endDate} viewingCurrentMonth={viewingCurrentMonth} />;
    $[2] = endDate;
    $[3] = profileId;
    $[4] = startDate;
    $[5] = viewingCurrentMonth;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t1 || $[9] !== t2 || $[10] !== t3) {
    t4 = <div className={t1}>{t2}{t3}</div>;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
}